import { useState } from 'react'
import { DatasetDocumentCover } from '@/components/library/DatasetDocumentCover'
import { DatasetDocumentChips } from '@/components/library/DatasetDocumentChips'
import { useTranslation } from 'react-i18next'
import { FolderPlus } from '@digital-twins/design-system/icons'
import Link from '@/routing/Link'
import { isContextMaterialIndexingInProgress } from '@digital-twins/shared-types'
import { KnowledgeDocumentWithStages } from '@/lib/api'
import { CardOverlayDeleteButton } from '@/components/ui/CardOverlayDeleteButton'
import { AddToCollectionMenu } from '@/components/library/AddToCollectionMenu'
import { coverImageAnchorClass, coverTone } from '@/components/library/LibraryItemCover'
import {
  LIBRARY_CARD_CHIP_CLASS,
  LIBRARY_CARD_COVER_CHIP_CLASS,
  LIBRARY_CARD_COVER_CLASS,
  LIBRARY_CARD_COVER_SCRIM_CLASS,
  LIBRARY_CARD_DESCRIPTION_CLASS,
  LIBRARY_CARD_META_CLASS,
  LIBRARY_CARD_TITLE_CLASS,
} from '@/components/library/libraryCardStyles'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { MarketLabel } from '@/components/ui/MarketLabel'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { KnowledgeStageFailureNotice } from './KnowledgeStageFailureNotice'
import { indexingStageLabel } from './KnowledgeIndexingBadge'
import {
  documentCoverUrl,
  documentDescription,
  documentTitle,
  documentTypeLabel,
  displayKnowledgeTypeLabel,
  facetValue,
  facetValues,
  fileKindLabel,
  marketDisplayEntries,
} from './libraryHelpers'

interface KnowledgeLibraryCardProps {
  doc: KnowledgeDocumentWithStages
  deleting: boolean
  onDelete: (token: string) => void
  onDocumentUpdated?: (doc: KnowledgeDocumentWithStages) => void
  /** Opens the document in the library's overlay pane instead of navigating to a new page. */
  onOpen?: (token: string) => void
  /** Position in the grid, for the entry point test ids. */
  index?: number
  /** Absent: no checkbox. `anySelected` keeps every checkbox visible. */
  selection?: KnowledgeLibraryCardSelection
}

export interface KnowledgeLibraryCardSelection {
  selected: boolean
  anySelected: boolean
  onToggle: () => void
}

export function KnowledgeLibraryCard({
  doc,
  deleting,
  onDelete,
  onDocumentUpdated,
  onOpen,
  index = 0,
  selection,
}: KnowledgeLibraryCardProps) {
  const { t } = useTranslation('app')
  const [confirmDelete, setConfirmDelete] = useState(false)
  // A page thumbnail taller than wide (a scanned report page) sits whole in
  // the 16:10 frame instead of being cropped to its top strip.
  const [isPortraitCover, setIsPortraitCover] = useState(false)
  const marketValues = facetValues(doc.facets, 'market')
  const year = facetValue(doc.facets, 'year')
  const brand = facetValue(doc.facets, 'brand')
  const typeLabel = displayKnowledgeTypeLabel(documentTypeLabel(doc))
  // One tone per type label, so types read by colour across the grid.
  const typeTone = coverTone(typeLabel.toLocaleLowerCase())
  const kind = fileKindLabel(doc)
  const inProgress = isContextMaterialIndexingInProgress(doc.indexingStatus)
  // Markets live in the footer, never as pills: a document can carry a dozen
  // of them and the pill row has to stay one line above the title.
  const markets = marketDisplayEntries(marketValues)
  const marketLabels = markets.map((market) => market.label)
  const coverSrc = documentCoverUrl(doc)
  const description = documentDescription(doc)
  const stageLabel = indexingStageLabel(doc.indexingStatus)
  const blockedReason = t('library.opensWhenReady', { stage: stageLabel })

  const marketMeta =
    markets.length === 0 ? null : markets.length === 1 ? (
      <MarketLabel
        label={markets[0].label}
        code={markets[0].code}
        data-testid={`knowledge-doc-market-${doc.token}`}
      />
    ) : (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          {/* asChild keeps this trigger a <span>: the whole card is already a
              <button> or an <a>, so a nested button would be invalid markup. */}
          <TooltipTrigger asChild>
            <span
              className="inline-flex items-center gap-1.5 underline decoration-dotted underline-offset-2"
              aria-label={t('library.marketsAria', { list: marketLabels.join(', ') })}
              data-testid={`knowledge-doc-market-${doc.token}`}
            >
              <CountryFlag code={null} />
              {t('library.marketCount', { count: markets.length })}
            </span>
          </TooltipTrigger>
          <TooltipContent
            className="max-w-[240px] text-[12px]"
            data-testid={`knowledge-doc-market-tooltip-${doc.token}`}
          >
            {marketLabels.join(', ')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )

  const cardBody = (
    <>
        <div className={`${LIBRARY_CARD_COVER_CLASS} aspect-[16/10] shrink-0`}>
          {coverSrc ? <img
            src={coverSrc}
            alt=""
            loading="lazy"
            decoding="async"
            className={
              isPortraitCover
                ? 'h-full w-full object-contain object-top px-6 pt-3 drop-shadow-sm transition-transform duration-300 group-hover:scale-[1.02]'
                : `h-full w-full object-cover ${coverImageAnchorClass(true)} transition-transform duration-300 group-hover:scale-[1.02]`
            }
            data-cover-shape={isPortraitCover ? 'portrait' : 'landscape'}
            onLoad={(event) => {
              const image = event.currentTarget
              setIsPortraitCover(image.naturalHeight > image.naturalWidth)
            }}
            data-testid={`knowledge-doc-cover-${doc.token}`}
          /> : <DatasetDocumentCover />}
          {/* A calm base under the type chip, so it reads on a busy or white page. */}
          <span
            aria-hidden="true"
            className={LIBRARY_CARD_COVER_SCRIM_CLASS}
          />
          {/* Only the type sits on the cover (with its tone dot): the brand
              lives in the meta row, where a long name can truncate cleanly. */}
          <div className="absolute inset-x-3 bottom-3 flex min-w-0 items-center gap-1.5 overflow-hidden">
            <Badge
              variant="muted"
              className={`${LIBRARY_CARD_CHIP_CLASS} ${LIBRARY_CARD_COVER_CHIP_CLASS} min-w-0 gap-1.5 overflow-hidden`}
              data-testid="knowledge-doc-pill-type"
            >
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${typeTone.swatch}`} aria-hidden="true" />
              <span className="truncate">{typeLabel}</span>
            </Badge>
            {doc.isScannedPdf ? (
              <Badge
                variant="warning"
                className={`${LIBRARY_CARD_CHIP_CLASS} shrink-0 bg-card text-chart-warning`}
                data-testid="knowledge-scanned-badge"
              >
                {t('library.noSearchableText')}
              </Badge>
            ) : null}
          </div>
          {inProgress ? (
            <div className="absolute inset-x-0 top-0 flex items-center gap-2 bg-media-scrim/45 px-3 py-2 text-[12px] text-media-scrim-foreground">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              {stageLabel}
            </div>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col gap-2 pt-3">
          <h3 className={LIBRARY_CARD_TITLE_CLASS}>{documentTitle(doc)}</h3>
          {description ? (
            <p
              className={LIBRARY_CARD_DESCRIPTION_CLASS}
              data-testid={`knowledge-doc-card-desc-${doc.token}`}
            >
              {description}
            </p>
          ) : null}
          <div
            className={`${LIBRARY_CARD_META_CLASS} flex-nowrap overflow-hidden [&>*]:shrink-0`}
            data-testid={`knowledge-doc-card-meta-${doc.token}`}
          >
            {brand ? (
              <>
                <span
                  className="!shrink min-w-0 truncate font-medium text-foreground/80"
                  title={brand}
                  data-testid={`knowledge-doc-card-brand-${doc.token}`}
                >
                  {brand}
                </span>
                <span aria-hidden="true">·</span>
              </>
            ) : null}
            {marketMeta}
            {year ? (
              <>
                {marketMeta ? <span aria-hidden="true">·</span> : null}
                <span>{year}</span>
              </>
            ) : null}
            {marketMeta || year ? <span aria-hidden="true">·</span> : null}
            <span>{kind}</span>
          </div>
        </div>
    </>
  )

  // Magazine card: no box, the cover carries the frame (D8).
  const surfaceClassName =
    'flex min-w-0 flex-col rounded-[14px] bg-transparent text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

  return (
    <div
      className="group relative flex min-w-0 flex-col"
      data-testid={`knowledge-doc-card-wrap-${doc.token}`}
    >
      {inProgress ? (
        <div
          aria-disabled="true"
          title={blockedReason}
          data-testid={`knowledge-doc-card-${doc.token}`}
          className={`${surfaceClassName} cursor-not-allowed opacity-75`}
        >
          {cardBody}
        </div>
      ) : onOpen ? (
        <button
          type="button"
          data-testid={`knowledge-doc-card-${doc.token}`}
          className={surfaceClassName}
          onClick={() => onOpen(doc.token)}
        >
          {cardBody}
        </button>
      ) : (
        <Link
          href={`/knowledge/${doc.token}`}
          data-testid={`knowledge-doc-card-${doc.token}`}
          className={surfaceClassName}
        >
          {cardBody}
        </Link>
      )}

      <DatasetDocumentChips document={doc} />

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 aspect-[16/10]" data-testid={`knowledge-doc-failure-overlay-${doc.token}`}>
        <KnowledgeStageFailureNotice
          doc={doc}
          variant="compact"
          className="pointer-events-auto absolute inset-x-3 bottom-12 max-h-[calc(100%_-_6rem)] overflow-y-auto rounded-lg bg-card/95 shadow-sm backdrop-blur-sm"
          onDocumentUpdated={onDocumentUpdated}
        />
      </div>

      {selection ? (
        <div
          className={`absolute left-3 top-3 z-20 rounded-md bg-card p-1 shadow-sm transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100 ${
            selection.selected || selection.anySelected ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selection.selected}
            onCheckedChange={() => selection.onToggle()}
            aria-label={t('library.collections.selection.select', { name: documentTitle(doc) })}
            data-testid={`knowledge-library-card-select-${index}`}
          />
        </div>
      ) : null}

      <div className="absolute right-3 top-3 z-20 flex items-center gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100">
        {inProgress ? null : (
          <AddToCollectionMenu
            items={[{ kind: 'document', token: doc.token }]}
            testId={`knowledge-library-card-add-menu-${index}`}
            trigger={
              <button
                type="button"
                aria-label={t('library.collections.addToCollection')}
                data-testid={`knowledge-library-card-add-to-collection-${index}`}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-foreground shadow-sm transition-colors hover:bg-card/80"
                onClick={(e) => e.stopPropagation()}
              >
                <FolderPlus className="h-4 w-4" aria-hidden="true" />
              </button>
            }
          />
        )}
        <CardOverlayDeleteButton
          ariaLabel={t('library.deleteAria', {
            name: doc.originalFilename || t('library.documentFallback'),
          })}
          dataTestId={`knowledge-doc-delete-${doc.token}`}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setConfirmDelete(true)
          }}
        />
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('library.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('library.deleteBody', {
                name: doc.originalFilename || t('library.untitled'),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{t('library.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              data-testid={`knowledge-doc-delete-confirm-${doc.token}`}
              onClick={() => onDelete(doc.token)}
            >
              {deleting ? t('library.deleting') : t('library.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
